import React, { useEffect, useState, useCallback } from 'react';
import Button from '../../../components/common/Button';
import GradientButton from '../../../components/common/GradientButton';
import Icon from '../../../components/common/Icon';
import Input from '../../../components/common/Input';
import Pagination from '../../../components/common/Pagination';
import Modal from '../../../components/common/Modal';
import Card from '../../../components/common/Card';
import StatusBadge from '../../../components/common/StatusBadge';
import JalaliDatePicker from '../../../components/common/JalaliDatePicker';
import StagesManager from './StagesManager';
import {
  getTournamentsApi,
  getTournamentApi,
  createTournamentApi,
  updateTournamentApi,
  getTournamentStandingsApi,
  registerClubToTournamentApi,
  updateRegistrationPaymentApi,
  approveRegistrationApi,
  rejectRegistrationApi,
} from '../../../api/tournament.api';
import { getClubsApi } from '../../../api/club.api';
import {
  TOURNAMENT_TYPE_LABELS,
  TOURNAMENT_STATUS_LABELS,
  REGISTRATION_STATUS_LABELS,
  PAYMENT_STATUS_LABELS,
  PAYMENT_STATUS_COLORS,
} from '../../../utils/constants';
import { toJalali, toJalaliFull } from '../../../utils/jalali';

const emptyForm = {
  name: '',
  type: 'LEAGUE',
  startDate: '',
  endDate: '',
  registrationDeadline: '',
  description: '',
};

const TOURNAMENT_BADGE_MAP = {
  OPEN: 'active',
  ONGOING: 'pending',
  COMPLETED: 'inactive',
  CLOSED: 'inactive',
};

const REGISTRATION_BADGE_MAP = {
  PENDING: 'pending',
  APPROVED: 'active',
  REJECTED: 'inactive',
};

export default function TournamentsManagement() {
  const [tournaments, setTournaments] = useState([]);
  const [clubs, setClubs] = useState([]);
  const [filters, setFilters] = useState({ type: '', status: '' });
  const [pagination, setPagination] = useState({ page: 1, limit: 10, total: 0, totalPages: 1 });
  const [formOpen, setFormOpen] = useState(false);
  const [editingTournament, setEditingTournament] = useState(null);
  const [form, setForm] = useState(emptyForm);
  const [error, setError] = useState('');
  const [snackbar, setSnackbar] = useState('');
  const [loading, setLoading] = useState(false);
  const [expandedId, setExpandedId] = useState(null);
  const [standings, setStandings] = useState({});
  const [registrations, setRegistrations] = useState({});
  const [registerDialog, setRegisterDialog] = useState(null);
  const [selectedClub, setSelectedClub] = useState('');
  const [paymentDialog, setPaymentDialog] = useState(null);
  const [decisionBusyId, setDecisionBusyId] = useState(null);
  const [stagesDialog, setStagesDialog] = useState(null); // { id, name }

  const loadClubs = useCallback(async () => {
    try {
      const { data } = await getClubsApi({ limit: 100 });
      setClubs(data.data);
    } catch {}
  }, []);

  const loadTournaments = useCallback(async () => {
    setLoading(true);
    try {
      const clean = Object.fromEntries(Object.entries(filters).filter(([, v]) => v !== ''));
      const { data } = await getTournamentsApi({ ...clean, page: pagination.page, limit: pagination.limit });
      setTournaments(data.data);
      setPagination(prev => ({ ...prev, total: data.meta.total, totalPages: data.meta.totalPages }));
    } catch (err) {
      setSnackbar(err.response?.data?.message || 'خطا در دریافت تورنمنت‌ ها');
    } finally {
      setLoading(false);
    }
  }, [filters, pagination.page, pagination.limit]);

  useEffect(() => { loadClubs(); }, []);
  useEffect(() => { loadTournaments(); }, [loadTournaments]);

  const fetchStandings = async (id) => {
    if (standings[id]) return;
    try {
      const { data } = await getTournamentStandingsApi(id);
      setStandings(prev => ({
        ...prev,
        [id]: { standings: data.data.standings, groups: data.data.groups },
      }));
    } catch {}
  };

  const fetchRegistrations = async (id) => {
    try {
      const { data } = await getTournamentApi(id);
      setRegistrations(prev => ({ ...prev, [id]: data.data.registrations || [] }));
    } catch {}
  };

  const toggleExpand = (id) => {
    if (expandedId === id) { setExpandedId(null); return; }
    setExpandedId(id);
    fetchStandings(id);
    fetchRegistrations(id);
  };

  const handleCreate = async (e) => {
    e.preventDefault();
    setError('');
    try {
      if (editingTournament) {
        await updateTournamentApi(editingTournament.id, form);
        setSnackbar('تورنمنت با موفقیت ویرایش شد');
      } else {
        await createTournamentApi(form);
        setSnackbar('تورنمنت با موفقیت ایجاد شد');
      }
      setFormOpen(false);
      setForm(emptyForm);
      setEditingTournament(null);
      loadTournaments();
    } catch (err) {
      setError(err.response?.data?.message);
    }
  };

  const handleRegister = async () => {
    if (!selectedClub || !registerDialog) return;
    try {
      await registerClubToTournamentApi(registerDialog, { clubId: Number(selectedClub) });
      setSnackbar('باشگاه با موفقیت ثبت‌ نام شد');
      setRegisterDialog(null);
      setSelectedClub('');
      loadTournaments();
      if (expandedId === registerDialog) fetchRegistrations(registerDialog);
    } catch (err) {
      setSnackbar(err.response?.data?.message);
    }
  };

  const handleDecision = async (tournamentId, registrationId, decision) => {
    setDecisionBusyId(registrationId);
    try {
      if (decision === 'approve') {
        await approveRegistrationApi(tournamentId, registrationId);
        setSnackbar('ثبت‌ نام باشگاه تایید شد');
      } else {
        await rejectRegistrationApi(tournamentId, registrationId);
        setSnackbar('ثبت‌ نام باشگاه رد شد');
      }
      await fetchRegistrations(tournamentId);
      loadTournaments();
    } catch (err) {
      setSnackbar(err.response?.data?.message || 'خطا در ثبت تصمیم');
    } finally {
      setDecisionBusyId(null);
    }
  };

  const handlePaymentUpdate = async (paymentStatus) => {
    if (!paymentDialog) return;
    try {
      await updateRegistrationPaymentApi(paymentDialog.tournamentId, paymentDialog.registrationId, paymentStatus);
      setSnackbar('وضعیت پرداخت بروزرسانی شد');
      setPaymentDialog(null);
      fetchRegistrations(paymentDialog.tournamentId);
    } catch (err) {
      setSnackbar(err.response?.data?.message);
    }
  };

  const handlePageChange = (newPage) => {
    if (newPage >= 1 && newPage <= pagination.totalPages) {
      setPagination(prev => ({ ...prev, page: newPage }));
    }
  };

  const getStatusBadge = (status) => (
    <StatusBadge status={TOURNAMENT_BADGE_MAP[status] || 'inactive'} label={TOURNAMENT_STATUS_LABELS[status] || status} />
  );

  return (
    <div className="space-y-6 animate-fade-in">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-black text-text-dark">مدیریت تورنمنت‌ ها</h1>
          <p className="text-sm text-slate-400">ایجاد، مدیریت، تایید ثبت‌ نام باشگاه‌ها و مشاهده جدول رده‌بندی</p>
        </div>
        <GradientButton onClick={() => { setEditingTournament(null); setForm(emptyForm); setError(''); setFormOpen(true); }}>
          <Icon name="plus" className="ml-2 h-4 w-4" />
          تورنمنت جدید
        </GradientButton>
      </div>

      <div className="flex flex-wrap gap-3">
        <select
          className="rounded-xl border-2 border-slate-200 p-2 text-sm outline-none transition-all duration-300 focus:border-emerald-500 focus:shadow-lg focus:shadow-emerald-500/20"
          value={filters.type}
          onChange={(e) => setFilters({ ...filters, type: e.target.value })}
        >
          <option value="">همه انواع</option>
          {Object.entries(TOURNAMENT_TYPE_LABELS).map(([k, v]) => (
            <option key={k} value={k}>{v}</option>
          ))}
        </select>
        <select
          className="rounded-xl border-2 border-slate-200 p-2 text-sm outline-none transition-all duration-300 focus:border-emerald-500 focus:shadow-lg focus:shadow-emerald-500/20"
          value={filters.status}
          onChange={(e) => setFilters({ ...filters, status: e.target.value })}
        >
          <option value="">همه وضعیت‌ ها</option>
          {Object.entries(TOURNAMENT_STATUS_LABELS).map(([k, v]) => (
            <option key={k} value={k}>{v}</option>
          ))}
        </select>
      </div>

      {snackbar && (
        <div className="rounded-xl bg-emerald-50 p-3 text-sm text-emerald-700 animate-fade-in">
          {snackbar}
          <button className="mr-2" onClick={() => setSnackbar('')}>✕</button>
        </div>
      )}

      <Card className="overflow-hidden">
        {loading ? (
          <div className="flex h-48 items-center justify-center">
            <div className="h-8 w-8 animate-spin rounded-full border-4 border-emerald-500 border-t-transparent" />
          </div>
        ) : (
          <>
            <div className="overflow-x-auto">
              <table className="w-full text-right text-sm">
                <thead className="bg-gradient-to-r from-emerald-50 to-white border-b border-slate-200">
                  <tr>
                    <th className="px-4 py-3 text-xs font-bold uppercase tracking-wider text-slate-500 w-10"></th>
                    <th className="px-4 py-3 text-xs font-bold uppercase tracking-wider text-slate-500">نام</th>
                    <th className="px-4 py-3 text-xs font-bold uppercase tracking-wider text-slate-500">نوع</th>
                    <th className="px-4 py-3 text-xs font-bold uppercase tracking-wider text-slate-500">شروع</th>
                    <th className="px-4 py-3 text-xs font-bold uppercase tracking-wider text-slate-500">پایان</th>
                    <th className="px-4 py-3 text-xs font-bold uppercase tracking-wider text-slate-500">مهلت ثبت‌ نام</th>
                    <th className="px-4 py-3 text-xs font-bold uppercase tracking-wider text-slate-500">وضعیت</th>
                    <th className="px-4 py-3 text-center text-xs font-bold uppercase tracking-wider text-slate-500">عملیات</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100">
                  {tournaments.map(t => {
                    const deadlinePassed =
                      t.registrationDeadline && new Date() > new Date(t.registrationDeadline);

                    return (
                    <React.Fragment key={t.id}>
                      <tr className="transition-colors duration-200 hover:bg-emerald-50/50">
                        <td className="px-4 py-3">
                          <button
                            onClick={() => toggleExpand(t.id)}
                            className="rounded p-1 transition hover:bg-slate-100"
                          >
                            <Icon
                              name="chevronDown"
                              className={`h-4 w-4 transition-transform duration-300 ${expandedId === t.id ? 'rotate-180' : ''}`}
                            />
                          </button>
                        </td>
                        <td className="px-4 py-3 font-medium">{t.name}</td>
                        <td className="px-4 py-3">{TOURNAMENT_TYPE_LABELS[t.type]}</td>
                        <td className="px-4 py-3">{toJalali(t.startDate)}</td>
                        <td className="px-4 py-3">{t.endDate ? toJalali(t.endDate) : '-'}</td>
                        <td className="px-4 py-3">
                          {t.registrationDeadline ? (
                            <span className={deadlinePassed ? 'text-red-500 font-bold' : ''}>
                              {toJalali(t.registrationDeadline)}
                              {deadlinePassed && ' (بسته شد)'}
                            </span>
                          ) : (
                            '-'
                          )}
                        </td>
                        <td className="px-4 py-3">{getStatusBadge(t.status)}</td>
                        <td className="px-4 py-3 text-center">
                          <div className="flex items-center justify-center gap-1">
                            <button
                              onClick={() => {
                                setEditingTournament(t);
                                setForm({
                                  name: t.name || '',
                                  type: t.type || 'LEAGUE',
                                  startDate: t.startDate || '',
                                  endDate: t.endDate || '',
                                  registrationDeadline: t.registrationDeadline || '',
                                  description: t.description || '',
                                });
                                setError('');
                                setFormOpen(true);
                              }}
                              className="rounded p-1 text-amber-500 transition-colors hover:bg-amber-50 hover:text-amber-700"
                              title="ویرایش تورنمنت"
                            >
                              <Icon name="edit" className="h-4 w-4" />
                            </button>
                            <button
                              onClick={() => setRegisterDialog(t.id)}
                              className="rounded p-1 text-emerald-500 transition-colors hover:bg-emerald-50 hover:text-emerald-700"
                              title="ثبت‌نام باشگاه"
                            >
                              <Icon name="users" className="h-4 w-4" />
                            </button>
                            <button
                              onClick={() => setStagesDialog({ id: t.id, name: t.name })}
                              className="rounded p-1 text-blue-500 transition-colors hover:bg-blue-50 hover:text-blue-700"
                              title="مدیریت مراحل و گروه‌ها"
                            >
                              <Icon name="trophy" className="h-4 w-4" />
                            </button>
                          </div>
                        </td>
                      </tr>
                      {expandedId === t.id && (
                        <tr>
                          <td colSpan="8" className="px-4 py-3 bg-slate-50/50">
                            <div className="space-y-5">
                              {/* ===== درخواست‌های عضویت باشگاه‌ها ===== */}
                              <div className="space-y-3">
                                <h4 className="font-bold text-text-dark">درخواست‌ های عضویت باشگاه‌ها</h4>
                                {registrations[t.id] ? (
                                  <div className="overflow-x-auto">
                                    <table className="w-full text-right text-sm">
                                      <thead>
                                        <tr className="border-b border-slate-200">
                                          <th className="px-3 py-2 text-xs font-bold text-slate-500">باشگاه</th>
                                          <th className="px-3 py-2 text-xs font-bold text-slate-500">تاریخ درخواست</th>
                                          <th className="px-3 py-2 text-xs font-bold text-slate-500">وضعیت</th>
                                          <th className="px-3 py-2 text-xs font-bold text-slate-500">پرداخت</th>
                                          <th className="px-3 py-2 text-center text-xs font-bold text-slate-500">عملیات</th>
                                        </tr>
                                      </thead>
                                      <tbody className="divide-y divide-slate-100">
                                        {registrations[t.id].map((reg) => {
                                          const canApprove = reg.paymentStatus === 'PAID';

                                          return (
                                          <tr key={reg.id}>
                                            <td className="px-3 py-2 font-medium">{reg.club?.name}</td>
                                            <td className="px-3 py-2 text-xs text-slate-500">
                                              {toJalali(reg.registrationDate)}
                                            </td>
                                            <td className="px-3 py-2">
                                              <StatusBadge
                                                status={REGISTRATION_BADGE_MAP[reg.status] || 'pending'}
                                                label={REGISTRATION_STATUS_LABELS[reg.status] || reg.status}
                                              />
                                            </td>
                                            <td className="px-3 py-2">
                                              <button
                                                onClick={() => setPaymentDialog({ tournamentId: t.id, registrationId: reg.id })}
                                                className={`rounded-lg px-2 py-1 text-xs font-bold ${PAYMENT_STATUS_COLORS[reg.paymentStatus]}`}
                                              >
                                                {PAYMENT_STATUS_LABELS[reg.paymentStatus]}
                                              </button>
                                            </td>
                                            <td className="px-3 py-2 text-center">
                                              {reg.status === 'PENDING' ? (
                                                <div className="flex flex-col items-center justify-center gap-1">
                                                  <div className="flex items-center justify-center gap-1">
                                                    <button
                                                      onClick={() => handleDecision(t.id, reg.id, 'approve')}
                                                      disabled={decisionBusyId === reg.id || !canApprove}
                                                      title={
                                                        canApprove
                                                          ? 'تایید عضویت'
                                                          : 'ابتدا وضعیت پرداخت را به «پرداخت‌ شده» تغییر دهید'
                                                      }
                                                      className="grid h-8 w-8 place-items-center rounded-xl text-emerald-600 transition hover:scale-110 hover:bg-emerald-100 disabled:opacity-30 disabled:hover:scale-100"
                                                    >
                                                      <Icon name="check" className="h-3.5 w-3.5" />
                                                    </button>
                                                    <button
                                                      onClick={() => handleDecision(t.id, reg.id, 'reject')}
                                                      disabled={decisionBusyId === reg.id}
                                                      title="رد عضویت"
                                                      className="grid h-8 w-8 place-items-center rounded-xl text-red-500 transition hover:scale-110 hover:bg-red-100"
                                                    >
                                                      <Icon name="close" className="h-3.5 w-3.5" />
                                                    </button>
                                                  </div>
                                                  {!canApprove && (
                                                    <span className="text-[10px] text-amber-600">
                                                      نیاز به تایید پرداخت
                                                    </span>
                                                  )}
                                                </div>
                                              ) : (
                                                <span className="text-xs text-slate-400">-</span>
                                              )}
                                            </td>
                                          </tr>
                                          );
                                        })}
                                        {registrations[t.id].length === 0 && (
                                          <tr>
                                            <td colSpan="5" className="py-4 text-center text-slate-400">
                                              هنوز باشگاهی درخواست عضویت نداده است
                                            </td>
                                          </tr>
                                        )}
                                      </tbody>
                                    </table>
                                  </div>
                                ) : (
                                  <div className="py-4 text-center text-slate-400">در حال بارگذاری...</div>
                                )}
                              </div>

                              {/* ===== جدول رده‌بندی ===== */}
                              <div className="space-y-3">
                                <h4 className="font-bold text-text-dark">جدول رده‌بندی</h4>
                                {standings[t.id] ? (
                                  (() => {
                                    const entry = standings[t.id];
                                    const groups = entry.groups;
                                    const flat = entry.standings || [];

                                    const renderTable = (rows) => (
                                      <div className="overflow-x-auto">
                                        <table className="w-full text-right text-sm">
                                          <thead>
                                            <tr className="border-b border-slate-200">
                                              <th className="px-3 py-2 text-xs font-bold text-slate-500">باشگاه</th>
                                              <th className="px-3 py-2 text-center text-xs font-bold text-slate-500">بازی</th>
                                              <th className="px-3 py-2 text-center text-xs font-bold text-slate-500">برد</th>
                                              <th className="px-3 py-2 text-center text-xs font-bold text-slate-500">مساوی</th>
                                              <th className="px-3 py-2 text-center text-xs font-bold text-slate-500">باخت</th>
                                              <th className="px-3 py-2 text-center text-xs font-bold text-slate-500">گل زده</th>
                                              <th className="px-3 py-2 text-center text-xs font-bold text-slate-500">گل خورده</th>
                                              <th className="px-3 py-2 text-center text-xs font-bold text-slate-500">تفاضل</th>
                                              <th className="px-3 py-2 text-center text-xs font-bold text-slate-500">امتیاز</th>
                                            </tr>
                                          </thead>
                                          <tbody className="divide-y divide-slate-100">
                                            {rows.map((row, idx) => (
                                              <tr key={row.club.id} className={idx % 2 === 0 ? 'bg-white' : 'bg-slate-50/30'}>
                                                <td className="px-3 py-2 font-medium">
                                                  {idx + 1}. {row.club.name}
                                                </td>
                                                <td className="px-3 py-2 text-center">{row.played}</td>
                                                <td className="px-3 py-2 text-center text-emerald-600">{row.won}</td>
                                                <td className="px-3 py-2 text-center text-amber-600">{row.drawn}</td>
                                                <td className="px-3 py-2 text-center text-red-500">{row.lost}</td>
                                                <td className="px-3 py-2 text-center">{row.goalsFor}</td>
                                                <td className="px-3 py-2 text-center">{row.goalsAgainst}</td>
                                                <td className="px-3 py-2 text-center font-mono">{row.goalDiff}</td>
                                                <td className="px-3 py-2 text-center font-bold text-emerald-600">{row.points}</td>
                                              </tr>
                                            ))}
                                            {rows.length === 0 && (
                                              <tr>
                                                <td colSpan="9" className="py-4 text-center text-slate-400">
                                                  هنوز مسابقه‌ای برگزار نشده است
                                                </td>
                                              </tr>
                                            )}
                                          </tbody>
                                        </table>
                                      </div>
                                    );

                                    if (groups && groups.length > 0) {
                                      return (
                                        <div className="space-y-5">
                                          {groups.map((group) => (
                                            <div key={group.id}>
                                              <p className="mb-1.5 text-xs font-bold text-slate-500">
                                                {group.name}
                                                {group.stageName ? ` (${group.stageName})` : ''}
                                              </p>
                                              {renderTable(group.standings || [])}
                                            </div>
                                          ))}
                                          {flat.length > 0 && (
                                            <div>
                                              <p className="mb-1.5 text-xs font-bold text-slate-500">بدون گروه</p>
                                              {renderTable(flat)}
                                            </div>
                                          )}
                                        </div>
                                      );
                                    }

                                    return renderTable(flat);
                                  })()
                                ) : (
                                  <div className="py-4 text-center text-slate-400">در حال بارگذاری...</div>
                                )}
                              </div>
                            </div>
                          </td>
                        </tr>
                      )}
                    </React.Fragment>
                    );
                  })}
                  {tournaments.length === 0 && (
                    <tr>
                      <td colSpan="8" className="py-8 text-center text-slate-400">
                        تورنمنتی ثبت نشده است
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
            <Pagination
              page={pagination.page}
              totalPages={pagination.totalPages}
              onPageChange={handlePageChange}
            />
          </>
        )}
      </Card>

      {/* مودال ایجاد تورنمنت */}
      <Modal open={formOpen} onClose={() => { setFormOpen(false); setEditingTournament(null); }} title={editingTournament ? 'ویرایش تورنمنت' : 'ایجاد تورنمنت جدید'} size="lg">
        <form onSubmit={handleCreate} className="space-y-4">
          {error && <div className="rounded-xl bg-red-50 p-3 text-sm text-red-600">{error}</div>}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <Input
              label="نام تورنمنت"
              value={form.name}
              onChange={(e) => setForm({ ...form, name: e.target.value })}
              required
              className="col-span-2"
            />
            <select
              className="w-full rounded-xl border-2 border-slate-200 p-3 text-sm outline-none transition-all duration-300 focus:border-emerald-500 focus:shadow-lg focus:shadow-emerald-500/20"
              value={form.type}
              onChange={(e) => setForm({ ...form, type: e.target.value })}
            >
              {Object.entries(TOURNAMENT_TYPE_LABELS).map(([k, v]) => (
                <option key={k} value={k}>{v}</option>
              ))}
            </select>
            <JalaliDatePicker
              label="تاریخ شروع"
              value={form.startDate}
              onChange={(date) => setForm({ ...form, startDate: date })}
              required
            />
            <JalaliDatePicker
              label="تاریخ پایان"
              value={form.endDate}
              onChange={(date) => setForm({ ...form, endDate: date })}
            />
            <JalaliDatePicker
              label="مهلت ثبت‌ نام (اختیاری)"
              value={form.registrationDeadline}
              onChange={(date) => setForm({ ...form, registrationDeadline: date })}
            />
            <Input
              label="توضیحات"
              value={form.description}
              onChange={(e) => setForm({ ...form, description: e.target.value })}
              className="col-span-2"
            />
          </div>
          <p className="text-[11px] text-slate-400">
            اگر مهلت ثبت‌ نام مشخص شود، مدیران باشگاه بعد از آن تاریخ دیگر نمی‌ توانند برای این تورنمنت درخواست عضویت بدهند.
          </p>
          <div className="flex justify-end gap-2 pt-2">
            <Button variant="ghost" type="button" onClick={() => { setFormOpen(false); setEditingTournament(null); }}>انصراف</Button>
            <GradientButton type="submit">{editingTournament ? 'ذخیره تغییرات' : 'ایجاد تورنمنت'}</GradientButton>
          </div>
        </form>
      </Modal>

      {/* مودال ثبت‌نام دستی باشگاه (توسط مدیر سیستم) */}
      <Modal open={Boolean(registerDialog)} onClose={() => setRegisterDialog(null)} title="ثبت‌نام باشگاه در تورنمنت">
        <div className="space-y-4">
          <p className="text-xs text-slate-400">
            این ثبت‌ نام دستی توسط مدیر سیستم انجام می‌ شود. مدیران باشگاه نیز می‌ توانند خودشان از پنل باشگاه درخواست عضویت بدهند که در همین لیست برای تایید ظاهر می‌ شود.
          </p>
          <select
            className="w-full rounded-xl border-2 border-slate-200 p-3 text-sm outline-none transition-all duration-300 focus:border-emerald-500 focus:shadow-lg focus:shadow-emerald-500/20"
            value={selectedClub}
            onChange={(e) => setSelectedClub(e.target.value)}
          >
            <option value="">انتخاب باشگاه</option>
            {clubs.map(c => (
              <option key={c.id} value={c.id}>{c.name}</option>
            ))}
          </select>
          <div className="flex justify-end gap-2">
            <Button variant="ghost" onClick={() => setRegisterDialog(null)}>انصراف</Button>
            <GradientButton onClick={handleRegister}>ثبت‌ نام</GradientButton>
          </div>
        </div>
      </Modal>

      {/* مودال تغییر وضعیت پرداخت */}
      <Modal open={Boolean(paymentDialog)} onClose={() => setPaymentDialog(null)} title="وضعیت پرداخت">
        <div className="space-y-3">
          {Object.entries(PAYMENT_STATUS_LABELS).map(([k, v]) => (
            <button
              key={k}
              onClick={() => handlePaymentUpdate(k)}
              className={`w-full rounded-xl px-3 py-2 text-right text-sm font-bold ${PAYMENT_STATUS_COLORS[k]}`}
            >
              {v}
            </button>
          ))}
        </div>
      </Modal>

      {/* مودال مدیریت مراحل و گروه‌ها */}
      <Modal
        open={Boolean(stagesDialog)}
        onClose={() => setStagesDialog(null)}
        title="مدیریت مراحل و گروه‌ها"
        size="full"
      >
        {stagesDialog && (
          <StagesManager tournamentId={stagesDialog.id} tournamentName={stagesDialog.name} />
        )}
      </Modal>
    </div>
  );
}
